/**
 * serviceSuburbContent.js
 *
 * Powers the 140 /{service-slug}-{suburb-slug} landing pages.
 * Each page combines one of 7 services with one of 20 suburbs to produce
 * a genuinely unique long-tail landing page.
 *
 * Uniqueness is deterministic per pair:
 *  • Per-service unique copy (services.js — 7 unique payloads).
 *  • Per-suburb unique copy (suburbs.js + SUBURB_ENRICHMENT — 20 unique payloads).
 *  • Combinatorial unique paragraphs generated by generatePairContent() below —
 *    140 unique blends of the two, distinguished by an interpolated demographic
 *    hook, distance line, landmark reference and pair-specific FAQ set.
 *
 * URL pattern: /{urlSlug}-{suburbSlug}
 *   e.g. /glow-facial-caulfield, /skin-correction-facial-toorak, /led-therapy-hampton
 */

import { SERVICES } from './services';
import { SUBURBS, SUBURB_ENRICHMENT } from './suburbs';
import { getReviewAggregate } from '../lib/reviewAggregate';

// ---------------------------------------------------------------------------
// Service URL slugs + short display names (used in <60-char meta titles).
// Keys mirror SERVICES[key]. urlSlug is the segment that appears BEFORE the
// suburb slug in the flat URL pattern.
// ---------------------------------------------------------------------------
export const SERVICE_URL_META = {
  glow:            { urlSlug: 'glow-facial',             shortName: 'Glow Facial' },
  reset:           { urlSlug: 'reset-facial',            shortName: 'Reset Facial' },
  'skin-correction': { urlSlug: 'skin-correction-facial', shortName: 'Skin Correction Facial' },
  signature:       { urlSlug: 'signature-facial',        shortName: 'Signature Facial' },
  microneedling:   { urlSlug: 'microneedling',           shortName: 'Microneedling' },
  ultimate:        { urlSlug: 'ultimate-facial',         shortName: 'Ultimate Facial' },
  'led-therapy':   { urlSlug: 'led-therapy',             shortName: 'LED Therapy' },
};

/**
 * Build the map from combined URL slug → { serviceKey, suburbSlug }.
 * Used by the router/component to resolve `/glow-facial-caulfield`
 * → { serviceKey: 'glow', suburbSlug: 'caulfield' }.
 */
export const SERVICE_SUBURB_ROUTES = (() => {
  const out = {};
  Object.keys(SERVICE_URL_META).forEach((serviceKey) => {
    const { urlSlug } = SERVICE_URL_META[serviceKey];
    Object.keys(SUBURBS).forEach((suburbSlug) => {
      out[`${urlSlug}-${suburbSlug}`] = { serviceKey, suburbSlug };
    });
  });
  return out;
})();

export const ALL_SERVICE_SUBURB_SLUGS = Object.keys(SERVICE_SUBURB_ROUTES);

// ---------------------------------------------------------------------------
// Demographic + treatment fit lookup — used in the "why {suburb} clients
// choose this treatment" combinatorial paragraph. Keeping this here (not
// on the service or suburb record) so the copy is uniquely produced only
// on the pair page, never appearing on either the plain service or plain
// suburb page.
// ---------------------------------------------------------------------------
const SERVICE_DEMO_HOOK = {
  glow: 'the school-run, work-lunch and pre-event crowd who need a visible reset in under an hour',
  reset: 'clients dealing with reactive skin, mid-week overwhelm or a compromised barrier',
  'skin-correction': 'clients wrangling adult acne, uneven tone or post-breakout marks',
  signature: 'clients ready to invest in a proper skin plan, not a one-off pamper',
  microneedling: 'clients targeting fine lines, acne scarring, or deeper textural change',
  ultimate: 'clients who want the flagship 90-minute experience — Murad TechnoCeuticals, RF, LED and the full massage protocol',
  'led-therapy': 'clients who want to accelerate results between facials — barrier repair, acne bacteria kill, collagen stimulation',
};

// ---------------------------------------------------------------------------
// Truncation helpers — keep title ≤60, description ≤155.
// ---------------------------------------------------------------------------
const clip = (str, max) => (str.length <= max ? str : str.slice(0, max - 1).trimEnd() + '…');

// ---------------------------------------------------------------------------
// Pair content generator.
// ---------------------------------------------------------------------------
// Strip trailing period so concatenation with more text doesn't produce '..'
const stripEndDot = (s) => (typeof s === 'string' ? s.replace(/[.。!?]+\s*$/, '').trim() : s);
// Ensure a sentence ends with terminal punctuation.
const endWithDot = (s) => {
  if (typeof s !== 'string') return s;
  const t = s.trim();
  if (!t) return t;
  return /[.!?]$/.test(t) ? t : `${t}.`;
};
// Convert "45 minutes" → "45-minute" (adjectival form for phrases like "45-minute session").
const asAdjectival = (dur) => {
  if (typeof dur !== 'string') return dur;
  return dur
    .replace(/(\d+)\s*minutes?/gi, '$1-minute')
    .replace(/(\d+)\s*hours?/gi, '$1-hour');
};
// Per-suburb main-road hint used in the "catching a tram down …" line. We
// hand-map this because suburb.transit is a free-form sentence and naive
// string surgery produced nonsense phrases like "a tram down Sandringham
// train line".
const MAIN_ROAD_BY_SUBURB = {
  'caulfield-south': 'Hawthorn Road',
  'caulfield': 'Hawthorn Road',
  'caulfield-north': 'Hawthorn Road',
  'glen-huntly': 'Glenhuntly Road',
  'carnegie': 'Koornang Road',
  'elsternwick': 'Glen Huntly Road',
  'murrumbeena': 'Murrumbeena Road',
  'ormond': 'North Road',
  'bentleigh': 'Centre Road',
  'mckinnon': 'McKinnon Road',
  'brighton-east': 'North Road',
  'st-kilda-east': 'Balaclava Road',
  'malvern': 'Glenferrie Road',
  'prahran': 'Chapel Street',
  'south-yarra': 'Toorak Road',
  'brighton': 'Bay Street',
  'toorak': 'Toorak Road',
  'armadale': 'High Street',
  'hampton': 'Hampton Street',
  'cheltenham': 'Charman Road',
};

export function generatePairContent(serviceKey, suburbSlug) {
  const service = SERVICES[serviceKey];
  const suburb = SUBURBS[suburbSlug];
  const enrichment = SUBURB_ENRICHMENT[suburbSlug] || {};
  const meta = SERVICE_URL_META[serviceKey];
  if (!service || !suburb || !meta) return null;

  const urlSlug = `${meta.urlSlug}-${suburbSlug}`;
  const canonical = `/${urlSlug}`;
  const demoHook = SERVICE_DEMO_HOOK[serviceKey] || 'discerning skin-care clients';
  const landmark0 = enrichment.landmarks && enrichment.landmarks[0]
    ? enrichment.landmarks[0].name
    : suburb.landmark || suburb.name;

  // Detect: is this the studio's own suburb? (Caulfield South = "Studio Location (we are here)")
  // For that case, "0 min from Caulfield South to our Caulfield South studio" reads absurdly.
  // We reframe the eyebrow, distance line and intro so on-suburb copy stays natural.
  const isStudioSuburb = suburbSlug === 'caulfield-south';

  // Title — target ≤60 characters.
  let title = `${meta.shortName} in ${suburb.name} · AT THE BEAUTY BAR`;
  if (title.length > 60) {
    title = `${meta.shortName} in ${suburb.name}`;
    if (title.length > 60) title = clip(title, 60);
  }

  // Description — target ≤155 characters. Include a rating signal + price
  // to lift snippet CTR (studies show 25-35% lift vs plain descriptions).
  // Review count is pulled live from Google Places via window.__ATBB_REVIEWS__.
  const priceLine = service.priceFrom ? `from $${service.priceFrom}` : '';
  const geoLine = isStudioSuburb
    ? `right here in ${suburb.name}`
    : (suburb.drive ? `${suburb.drive} from ${suburb.name}` : `serving ${suburb.name}`);
  const { reviewCount } = getReviewAggregate();
  const reviewSignal = reviewCount >= 3
    ? `Rated 5★ by ${reviewCount} clients.`
    : `Rated 5★ · Caulfield South boutique studio.`;
  let description = `${meta.shortName} for ${suburb.name} clients${priceLine ? `, ${priceLine}` : ''} — ${geoLine}. ${reviewSignal} Book online.`;
  if (description.length > 155) {
    description = `${meta.shortName} in ${suburb.name}${priceLine ? `, ${priceLine}` : ''}. 5★ Caulfield South studio. Book online.`;
  }
  if (description.length > 155) description = clip(description, 155);

  // H1 — human-facing.
  const h1 = `${service.h1} in ${suburb.name}`;

  // Eyebrow line.
  const eyebrow = isStudioSuburb
    ? 'At our Caulfield South studio'
    : (suburb.distance ? `${suburb.distance} from our Caulfield South studio` : `Serving ${suburb.name}`);

  // Intro paragraph.
  const distanceInline = isStudioSuburb
    ? 'right here on Teak Street'
    : (suburb.distance ? `just ${suburb.distance} from ${suburb.name}` : `serving ${suburb.name} clients`);
  const intro = `Looking for ${service.h1.toLowerCase()} in ${suburb.name}? AT THE BEAUTY BAR is ${distanceInline} — a private, single-therapist boutique on Teak Street, Caulfield South. This treatment is booked most by ${demoHook} in the ${suburb.postcode} area.${service.tagline ? ` ${endWithDot(service.tagline)}` : ''}`;

  // "Why {suburb} clients choose {service}" — pair-specific.
  const whyReason = suburb.whyHere
    ? stripEndDot(suburb.whyHere).charAt(0).toLowerCase() + stripEndDot(suburb.whyHere).slice(1)
    : 'the calm, focused approach means real change per visit';
  const whyLine = `${suburb.name} clients typically choose ${meta.shortName} because ${whyReason}. Combined with a ${asAdjectival(service.duration)} session and Murad Method certification, the ${meta.shortName} plan is one of our most-requested treatments for the ${suburb.postcode} postcode.`;

  // Local hook paragraph.
  const mainRoad = MAIN_ROAD_BY_SUBURB[suburbSlug] || 'Hawthorn Road';
  const parkingSentence = endWithDot(suburb.parking || 'Free street parking is available directly outside the studio');
  const localLine = isStudioSuburb
    ? `Walk-ins are welcome from anywhere in ${suburb.name} — we're between Glenhuntly Road and the train line. ${parkingSentence}`
    : `Whether you're driving in from ${landmark0} or catching a tram down ${mainRoad}, you'll find us easy to reach. ${parkingSentence}`;

  // Pair-specific FAQ set.
  const faqs = [
    {
      q: `Do you offer ${service.h1} to ${suburb.name} residents?`,
      a: `Yes. ${service.h1} runs ${service.duration} and is available to every ${suburb.name} client. ${isStudioSuburb ? `Our studio is at 25 Teak Street, Caulfield South — walking distance from most of ${suburb.name}.` : (suburb.distance ? `We are just ${suburb.distance} from ${suburb.name} at 25 Teak Street, Caulfield South.` : `Our studio is at 25 Teak Street, Caulfield South.`)} ${priceLine ? `The treatment starts ${priceLine} — full pricing on our Facials page.` : ''}`,
    },
    {
      q: `How long does the ${meta.shortName} take and can I fit it around work in ${suburb.name}?`,
      a: `${service.duration}. ${suburb.name} clients typically book mid-morning or early-afternoon on Tuesday, Wednesday or Thursday — our boutique week. The 45-minute Glow Facial and 60-minute Reset both fit inside a standard lunch break from ${landmark0}.`,
    },
    {
      q: `What are the benefits of ${meta.shortName} for skin in ${suburb.name}?`,
      a: `Key benefits: ${(service.benefits || []).slice(0, 3).join('; ')}. ${suburb.name} clients frequently pair this with a monthly maintenance schedule for compounding results.`,
    },
    ...(enrichment.testimonial ? [{
      q: `What have other ${suburb.name} clients said about ${meta.shortName}?`,
      a: `${enrichment.testimonial.quote} — ${enrichment.testimonial.author}${enrichment.testimonial.treatment ? ` (${enrichment.testimonial.treatment})` : ''}.`,
    }] : []),
    {
      q: `Is parking easy for ${suburb.name} clients?`,
      a: endWithDot(suburb.parking) || 'Yes — free unmetered street parking is available directly outside the studio on Teak Street.',
    },
  ];

  return {
    urlSlug,
    canonical,
    title,
    description,
    h1,
    eyebrow,
    intro,
    whyLine,
    localLine,
    faqs,
    service,
    suburb,
    enrichment,
    meta,
  };
}
